import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, debounceTime, distinctUntilChanged, filter, Subscription, take } from 'rxjs';
import {
  selectBlackList,
  selectCaseApproved,
  selectPatient,
  selectReadonly,
} from '../../../../store/selectors/common-case.selector';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Option } from '../../../../../../ui-components/core-dropdown/core-dropdown.component';
import { PatientCardComponent } from '../../../../../../shared/components/patient-card/patient-card.component';
import { PatientCreateComponent } from '../../../../../definition/patient/patient-create/patient-create.component';
import { validateTCKN } from '../../../../../../shared/services/validators/validateTCKN';
import { AppMessageService } from '../../../../../../shared/services/app-message/app-message.service';
import { selectPatientInformations } from '../../../../store/selectors/patient-informations.selector';
import {
  selectDropdownLoaded,
  selectDropdownOptions,
} from '../../../../store/selectors/dropdowns.selector';
import { IPatient } from '../../../../../definition/patient/interface/patient.interface';
import { PatientPhonePopupComponent } from './patient-phone-popup/patient-phone-popup.component';
import { PatientEmailPopupComponent } from './patient-email-popup/patient-email-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { ICasePatientInformations } from '../../../../interfaces/interfaces';
import { MernisQueryService } from '../../../../../../shared/components/mernis-query/services/mernis-query.service';
import { AppAlertService } from '../../../../../../shared/services/app-alert/app-alert.service';

@Component({
  selector: 'app-patient-informations',

  templateUrl: './patient-informations.component.html',
  styleUrl: './patient-informations.component.scss',
})
export class PatientInformationsComponent implements OnInit, OnDestroy {
  @ViewChild('patientCard') patientCard: PatientCardComponent;
  @ViewChild('createPatient') createForm: PatientCreateComponent;
  @ViewChild('phonePopup') phonePopup: PatientPhonePopupComponent;
  @ViewChild('emailPopup') emailPopup: PatientEmailPopupComponent;

  countries: Option[] = [];
  bloodGroups: Option[] = [];
  @Output() formChanged = new EventEmitter();
  subscriptions = new Subscription();
  form: FormGroup;
  blackList: string[] = [];
  hiddenFields: { [key: string]: boolean } = {}; // Gizlenmesi gereken alanlar

  showDialog: boolean = false;
  lockInput: boolean = false;
  // icdCodeList: any[];
  genders = [
    { label: this.translate.instant('gender.male'), value: 'Male' },
    { label: this.translate.instant('gender.female'), value: 'Female' },
  ];

  phoneTypes: Option[];
  patientPhones: any[];
  patientPhonesCount: any[];

  emailTypes: Option[];
  patientEmails: any[];
  patientEmailsCount: any[];

  selectedPatientMedicalRecords: any[];

  selectedPatientEmails: any[];
  isReadOnly: boolean = false;
  readonlyInformations: ICasePatientInformations | null;
  maxDate = new Date();
  isCaseApproved: boolean | null;

  private formSubscriptions = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly fb: FormBuilder,
    private readonly messageService: AppMessageService,
    private readonly translate: TranslateService,
    private readonly mernisQueryService: MernisQueryService,

  private readonly alertService: AppAlertService,

  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      patientNo: new FormControl(null),
      firstName: new FormControl(null),
      lastName: new FormControl(null),
      idNumber: new FormControl(null, [Validators.pattern(/^\d{11}$/),  validateTCKN]),
      unidentified: new FormControl(null),
      birthDate: new FormControl(null),
      age: new FormControl(null),
      gender: new FormControl(null),
      bloodGroup: new FormControl(null),
      isForeignNational: new FormControl(null),
      country: new FormControl(null),
      passportNo: new FormControl(null),
      description: new FormControl(null),
      medicalRecords: new FormControl([]),
      contacts: new FormControl([]),
      idNumberVerified: new FormControl(false),
      pickupAddress: new FormControl(null),
    });

    this.listenForm();

    combineLatest([
      this.store.pipe(select(selectDropdownOptions('countries'))),
      this.store.pipe(select(selectDropdownOptions('bloodGroups'))),
      // this.store.pipe(select(selectDropdownOptions('icdCodeList'))),
      this.store.pipe(select(selectDropdownOptions('phoneTypes'))),
      this.store.pipe(select(selectDropdownOptions('emailTypes'))),
      this.store.pipe(select(selectDropdownLoaded)),
    ])
      .pipe(
        // loaded flag'ı true olduğunda (yani veriler yüklendiğinde) veri akışı devam eder
        filter(([, , ,  , loaded]) => loaded),
        take(1)
      )
      .subscribe(
        ([countries, bloodGroups, phoneTypes, emailTypes]) => {// icdCodeList
          this.countries = countries;
          this.bloodGroups = bloodGroups;
          // this.icdCodeList = icdCodeList;
          this.phoneTypes = phoneTypes;
          this.emailTypes = emailTypes;
        }
      );

       this.subscriptions.add(
    this.store.select(selectPatientInformations)
      .pipe(distinctUntilChanged())
      .subscribe(patientInformations => {
        this.readonlyInformations = patientInformations
          ? { ...patientInformations }
          : null;
          let age: number | null;

          if (patientInformations?.age != null) {
            age = patientInformations.age;
          } else if (patientInformations?.birthDate) {
            age = this.calculateAge(new Date(patientInformations.birthDate));
          } else {
            age = null;
          }
        this.form.patchValue({
          firstName: patientInformations?.firstName ?? null,
          lastName: patientInformations?.lastName ?? null,
          patientNo: patientInformations?.patientNo ?? null,
          idNumber: patientInformations?.idNumber ?? null,
          unidentified: patientInformations?.unidentified,
          gender: patientInformations?.gender ?? null,
          passportNo: patientInformations?.passportNo ?? null,
          bloodGroup: patientInformations?.bloodGroup?.value ?? null,
          isForeignNational: patientInformations?.isForeignNational ?? null,
          country: patientInformations?.country?.value ?? null,
          birthDate: patientInformations?.birthDate
            ? new Date(patientInformations.birthDate)
            : null,
          age:age,
          description: patientInformations?.description ?? null,
          medicalRecords: patientInformations?.medicalRecords,
          contacts: patientInformations?.contacts,
          idNumberVerified: patientInformations?.idNumberVerified ?? false,
        });

        this.form.markAsPristine();
        if (patientInformations?.patientNo) {
          const fieldsToDisable = [
            'firstName',
            'lastName',
            'idNumber',
            'birthDate',
            'gender',
            'passportNo',
            'isForeignNational',
            'country',
            'age',
          ];
          for(const field of fieldsToDisable){
            this.form.get(field)?.disable()
          }

          setTimeout(() => {
            this.patientCard?.setPatientValue(patientInformations.patientNo!);
          }, 20);
        }
      })
  );

  // Hasta entity'si - telefon ve email
    this.subscriptions.add(
      this.store.select(selectPatient)
        .pipe(distinctUntilChanged())
        .subscribe(patient => {
          if (!patient) {
            this.patientPhones = [];
            this.patientEmails = [];
            this.patientPhonesCount = [];
            this.patientEmailsCount = [];
            return;
          }

          this.patientEmailsCount =
            patient.contacts
              ?.filter(c => c.email)
              ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];

          this.patientPhonesCount =
            patient.contacts
              ?.filter(c => c.phone)
              ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];

          // Burada sadece sayım / popup başlangıç verisi için doldur
          // priority filtresi istersen burada da aynı mantığı koru
          this.patientPhones =
            patient.contacts
              ?.filter(c => c.phone && c.priority === true)
              ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];

          this.patientEmails =
            patient.contacts
              ?.filter(c => c.email && c.priority === true)
              ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];
        })
    );

  // Kara liste
  this.subscriptions.add(
    this.store.select(selectBlackList)
      .pipe(distinctUntilChanged())
      .subscribe(blackList => {
        this.blackList = blackList;
        this.updateHiddenFields();
      })
  );

  // Readonly
  this.subscriptions.add(
    this.store.select(selectReadonly)
      .pipe(distinctUntilChanged())
      .subscribe(isReadOnly => {
        this.isReadOnly = isReadOnly;
      })
  );

  // Onaylı mı?
  this.subscriptions.add(
    this.store.select(selectCaseApproved)
      .pipe(distinctUntilChanged())
      .subscribe(isApproved => {
        this.isCaseApproved = isApproved;
      })
  );
}

  onIdNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replaceAll(/\D/g, '');
    if (input.value.length > 11) {
      input.value = input.value.slice(0, 11);
    }
    this.form.get('idNumber')?.setValue(input.value, { emitEvent: false });
  }


  get phones(): any[] {
    return (
      this.form
        ?.get('contacts')
        ?.value?.filter((contact: any) => contact.phone) || []
    );
  }

  get emails(): any[] {
    return (
      this.form
        ?.get('contacts')
        ?.value?.filter((contact: any) => contact.email) || []
    );
  }

  updatePhoneItems(updatedPhones: any[]): void {
    const allContacts = this.form?.get('contacts')?.value || [];
    const nonPhoneContacts = allContacts.filter(
      (contact: any) => !contact.phone
    );

    const updatedPhoneContacts = updatedPhones.map((phone) => ({
      ...phone,
    }));

    const uniquePhonesMap = new Map();
    for(const p of updatedPhoneContacts){
       uniquePhonesMap.set(p.phone + p.prefix, { ...p }); // Aynı phone varsa üzerine yazar, böylece uniq olur
    }

    const uniquePhones = Array.from(uniquePhonesMap.values());

    this.form.patchValue({
      contacts: [...nonPhoneContacts, ...uniquePhones],
    });
  }

  updateEmailItems(updatedEmails: any[]): void {
    const allContacts = this.form?.get('contacts')?.value || [];
    const nonEmailContacts = allContacts.filter(
      (contact: any) => !contact.email
    );

    const updatedEmailContacts = updatedEmails.map((email) => ({
      ...email,
    }));

    const uniqueEmailsMap = new Map();
    for(const e of updatedEmailContacts){
       uniqueEmailsMap.set(e.email, { ...e }); // Aynı phone varsa üzerine yazar, böylece uniq olur
    }

    const uniqueEmails = Array.from(uniqueEmailsMap.values());

    this.form.patchValue({
      contacts: [...nonEmailContacts, ...uniqueEmails],
    });
  }

  listenForm() {
    if (this.formSubscriptions) {
      this.formSubscriptions.unsubscribe();
    }
    this.formSubscriptions = new Subscription(); // yeni bir set başlat

    this.formSubscriptions.add(
      this.form
        .get('isForeignNational')
        ?.valueChanges.subscribe((isForeign) => {
          if (isForeign) {
            this.form.get('country')?.enable();
          } else {
            this.form.get('country')?.setValue(null);
            this.form.get('country')?.disable();
          }
        })
    );

    this.formSubscriptions.add(
      this.form.get('birthDate')?.valueChanges.subscribe((birthDate) => {
        if (birthDate) {
          const age = this.calculateAge(new Date(birthDate));
          this.form.get('age')?.setValue(age, { emitEvent: false });
          this.form.get('age')?.disable();
        } else {
          this.form.get('age')?.setValue(null, { emitEvent: false });
          this.form.get('age')?.enable();
        }
      })
    );

    this.formSubscriptions.add(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        if (this.form.dirty) {
          this.formChanged.emit();
        }
      })
    );
  }

  onOpenDetail(patientDetail: any) {
    this.showDialog = true;
    setTimeout(() => {
      this.createForm.initialize(patientDetail);
    }, 500);
  }

  onPhoneSearchClicked() {
    this.phonePopup.initilize(this.phoneTypes, this.patientPhonesCount);
  }

  onEmailSearchClicked() {
    this.emailPopup.initilize(this.emailTypes, this.patientEmailsCount);
  }

  savePhone(selectedPhones: any[]): void {
  const allContacts = this.form?.get('contacts')?.value || [];
  const emails = allContacts.filter((contact: any) => contact.email);
  // Sadece gelen selectedPhones'u kullan, formPhones ile birleştirme
  // çünkü initilizeAndSave zaten tüm listeyi gönderiyor
  const uniquePhonesMap = new Map();
  for (const p of selectedPhones) {
    uniquePhonesMap.set(p.phone + p.prefix, { ...p });
  }
  const uniquePhones = Array.from(uniquePhonesMap.values());

  this.form.patchValue({
    contacts: [...emails, ...uniquePhones],
  });
  this.form.markAsDirty();
}


saveEmail(selectedEmails: any[]): void {
  const allContacts = this.form?.get('contacts')?.value || [];
  const phones = allContacts.filter((contact: any) => contact.phone);

  const uniqueEmailsMap = new Map();
  for (const e of selectedEmails) {
    uniqueEmailsMap.set(e.email, { ...e });
  }
  const uniqueEmails = Array.from(uniqueEmailsMap.values());

  this.form.patchValue({
    contacts: [...phones, ...uniqueEmails],
  });
  this.form.markAsDirty();
}

  searchIdNumber() {

  const birthDay=this.form.controls['birthDate'].value ? new Date(this.form.controls['birthDate'].value) : null;
  if(!birthDay){
    this.messageService.warningToast('','Doğum Tarihi Girilmemiş!');
    return;
  }
  const year=birthDay.getFullYear()
  const month=birthDay.getMonth()+1;
  const day=birthDay.getDate();

      this.mernisQueryService.getMernisUserData({
        tckn:this.form.controls['idNumber'].value,
        birthYear:year?.toString(),
        birthMonth:month?.toString(),
        birthDay:day?.toString()
      }).subscribe({
          next: (res: any) => {


            if (!res.isVerified) {
              console.error('Doğrulama Hatası:', res.errorMessage);
            }
          },
          error:(error) => {
            this.form.patchValue({
              idNumberVerified:false
            })
          }
      });
  }

  onPatientSelect(patient: IPatient | null) {
    console.log('Selected patient:', patient);
    if (!patient || !patient.id) {
      this.clearSelectedPatient();
      return;
    }

    if (this.hasActiveSpecialCategoryTags(patient)) {
      const activeTags = this.getActiveSpecialCategoryTags(patient);
      const alarmTags = activeTags.filter((tag) => this.toBoolean(tag?.alarm));



      this.alertService.sweetConfirm({
        header: 'Özel Kategori Etiketi Bulundu',
        html: alarmTags.map((tag) => `<div style="padding: 4px 0; border-bottom: 1px solid #f0f0f0;">${tag.label}</div>`).join(''),
        type: 'danger' as any,
        icon: 'error' as any,
        showCloseButton: true,
        acceptLabel:"Tamam / Devam Et",
        accept: () => {
          this.applySelectedPatient(patient);
        },
        reject: () => {
          this.clearSelectedPatient();
        },
      } as any);

      return;
    }

    this.applySelectedPatient(patient);
  }

  private applySelectedPatient(patient: IPatient | null) {
    this.selectedPatientMedicalRecords = [];
    this.patientPhones = [];
    this.patientEmails = [];
    this.patientEmailsCount = [];
    this.patientPhonesCount = [];

    this.form.patchValue({
      contacts: [],
      medicalRecords: [],
    });

    if (patient) {
      const priorityAddress = patient.addresses?.find(
        (addr) => addr.isPriority === true
      );

      this.patientPhones =
        patient?.contacts
          ?.filter((c) => c.phone && c.priority === true)
          ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];

      this.patientEmails =
        patient?.contacts
          ?.filter((c) => c.email && c.priority === true)
          ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];

      this.patientEmailsCount =
        patient?.contacts
          ?.filter((c) => c.email)
          ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];

      this.patientPhonesCount =
        patient?.contacts
          ?.filter((c) => c.phone)
          ?.map(({ id, ...rest }) => ({ ...rest })) ?? [];

      this.form.patchValue({
        medicalRecords: patient?.medicalRecords,
        pickupAddress: priorityAddress,
      });

      this.selectedPatientMedicalRecords = patient?.medicalRecords;

      this.phonePopup.initilizeAndSave(this.phoneTypes, this.patientPhones);
      this.emailPopup.initilizeAndSave(this.emailTypes, this.patientEmails);

     // console.log(this.patientPhonesCount, this.patientEmailsCount)
      /**patient içindeki addresses arrayinin ilk elemanını

       */
      // Hasta bilgileri varsa, formu güncelle ve inputları disable et
      let age: number | null = null;

      if (patient?.age != null) {
        age = patient.age;
      } else if (patient?.birthDate) {
        age = this.calculateAge(new Date(patient.birthDate));
      }

      this.form.patchValue({
        firstName: patient?.firstName ?? null,
        lastName: patient?.lastName ?? null,
        patientNo: patient?.id ?? null,
        idNumber: patient?.idNumber ?? null,
        gender: patient?.gender ?? null,
        passportNo: patient?.passportNo ?? null,
        isForeignNational: patient?.isForeignNational ?? null,
        country: patient?.countryId ?? null,
        birthDate: patient?.birthDate ? new Date(patient.birthDate) : null,
        age,
        idNumberVerified: patient?.idNumberVerified ?? false,
      });

      this.form.markAsDirty();
      this.form.updateValueAndValidity();

      this.disablePatientFields();

      if (patient.id) {
        this.patientCard.setPatientValue(patient.id);
      }

      return;
    }

    this.form.reset();
    this.form.markAsDirty();
    this.form.updateValueAndValidity();

    this.enablePatientFields();
  }
 private clearSelectedPatient(): void {
  this.selectedPatientMedicalRecords = [];
  this.patientPhones = [];
  this.patientEmails = [];
  this.patientEmailsCount = [];
  this.patientPhonesCount = [];

  this.enablePatientFields();

  this.form.patchValue({
    firstName: null,
    lastName: null,
    patientNo: null,
    idNumber: null,
    gender: null,
    passportNo: null,
    isForeignNational: null,
    country: null,
    birthDate: null,
    age: null,
    idNumberVerified: false,
    contacts: [],
    medicalRecords: [],
    pickupAddress: null,
  });

  this.form.get('patientNo')?.setValue(null);
  this.form.get('patientNo')?.markAsDirty();
  this.form.get('patientNo')?.updateValueAndValidity();

  this.form.markAsDirty();
  this.form.updateValueAndValidity();

  this.phonePopup?.initilizeAndSave(this.phoneTypes, []);
  this.emailPopup?.initilizeAndSave(this.emailTypes, []);

  /**
   * PatientCard içindeki seçili hasta datasını da temizle.
   */
  this.patientCard?.clearPatient?.();
}

  // Yaşı hesaplamak için yardımcı fonksiyon
  calculateAge(birthDate: Date): number {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  }

  updateHiddenFields(): void {
    for (const key of Object.keys(this.form.controls)) {
      this.hiddenFields[key] = this.blackList.includes(key);
    }
  }

  getFormValues() {
    const check: any = this.form.controls['idNumber']?.errors ?? null;

    if (check?.invalidTCKN) {
      this.messageService.errorToast('Başarısız', 'Tckn doğru değil');
      return false;
    }
    return this.form.getRawValue();
  }

  get medicalRecords(): any[] {
    return this.form?.get('medicalRecords')?.value || [];
  }

  updateMedicalRecords(updatedMedicalRecords: any[]) {
    this.form.patchValue({
      medicalRecords: updatedMedicalRecords,
    });
    this.form.markAsDirty();
  }

  private disablePatientFields(): void {
    [
      'firstName',
      'lastName',
      'idNumber',
      'birthDate',
      'gender',
      'passportNo',
      'isForeignNational',
      'country',
      'age',
    ].forEach((field) => this.form.get(field)?.disable());
  }

  private enablePatientFields(): void {
    [
      'firstName',
      'lastName',
      'idNumber',
      'birthDate',
      'gender',
      'passportNo',
      'isForeignNational',
      'country',
      'age',
    ].forEach((field) => this.form.get(field)?.enable());
  }
  private getActiveSpecialCategoryTags(patient: IPatient): any[] {
    const tags = this.normalizeSpecialCategoryTags(
      (patient as any)?.specialCategoryTags ?? []
    );

    return tags.filter((tag) => !this.toBoolean(tag?.passive));
  }

  private hasActiveSpecialCategoryTags(patient: IPatient): boolean {
    return this.getActiveSpecialCategoryTags(patient).length > 0;
  }

  private normalizeSpecialCategoryTags(tags: any[]): any[] {
    return (Array.isArray(tags) ? tags : [])
      .map((tag) => this.parseMaybeJson(tag))
      .filter(Boolean)
      .map((tag) => ({
        ...tag,
        label: tag?.label ?? tag?.name ?? tag?.tag ?? '',
        alarm: this.toBoolean(tag?.alarm),
        passive: this.toBoolean(tag?.passive),
        warning: this.toBoolean(tag?.warning),
      }));
  }

  private parseMaybeJson(value: any): any {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === 'string' ? this.parseMaybeJson(parsed) : parsed;
    } catch {
      return value;
    }
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }


  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event): void {
    this.unSub();
  }

  unSub() {
    if (this.formSubscriptions) {
      this.formSubscriptions.unsubscribe();
    }
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unSub();
  }
}
